import { SiteNotConfigured } from '@pa/ui'
import { StrictMode } from 'react'
import { createRoot, type Root } from 'react-dom/client'
import { createPatientQueryClient, setupApi } from './api'
import { App } from './App'
import { parseEnv } from './env'
import { resolveMode } from './mode'
import { createHostBridge, takeNonce } from '@/features/frame-bridge'

/**
 * Validates the environment, configures the API once and mounts the app (Frontend §1.6). An invalid config
 * renders "This site isn't configured" and logs the zod issues.
 */
export async function boot(rawEnv: Record<string, unknown>, container: HTMLElement): Promise<Root> {
  const root = createRoot(container)
  const result = parseEnv(rawEnv)
  if (!result.ok) {
    const summary = result.issues.map((i) => `${i.path.join('.') || '(env)'}: ${i.message}`).join('\n')
    console.error(`This site isn't configured. Invalid environment:\n${summary}`, result.issues)
    root.render(<SiteNotConfigured />)
    return root
  }
  const { env } = result
  setupApi(env)
  // The embed iframe listens for embed.js's `init` before anything async (Frontend §4.3 handshake, task 08).
  const mode = resolveMode(window.location.pathname, env.VITE_DEFAULT_CLINIC_SLUG)
  const bridge =
    mode.kind === 'embed' && window.parent !== window
      ? createHostBridge({ win: window, parent: window.parent, nonce: takeNonce(window) })
      : undefined
  // `import.meta.env.VITE_ENV` is replaced at build time, so a production build drops the mock chunk (task 19).
  if (import.meta.env.VITE_ENV !== 'prod' && env.VITE_API_MOCK) {
    const { startMockWorker } = await import('./mock')
    await startMockWorker(env.VITE_API_ORIGIN)
  }
  root.render(
    <StrictMode>
      <App queryClient={createPatientQueryClient()} env={env} {...(bridge && { bridge })} />
    </StrictMode>,
  )
  return root
}
